// combatpro/panels/monsters/studio/useMonsterStudioImportExport.js
//
// Combat PRO - Monster Studio import / export runtime (Stage 60: Import / Export).
//
// The import / export runtime, expressed as a React hook. It owns file handling,
// JSON parsing, JSON generation, import validation, and import orchestration - and
// nothing else. It consumes the Stage 55 engine for normalization and the Stage 56
// editor's load seam; it recreates neither. It performs no cloud save, no CRUD, no
// versioning, and no encounter work.
//
// Faithful to the legacy (battletracker.html):
//   export <- viStudioExport 11287: pretty JSON (2-space) of the current model,
//             downloaded as "(customName||name||monster, slugged)".json via a
//             throwaway anchor.
//   import <- viStudioImport 11296 + the hidden #vi-st-import-file input +
//             viStudioImportFile 11299: read the file as text, JSON.parse, normalize
//             through the engine, mint a fresh pool id when the file carries no
//             customMonsterId, take versions from the file (or []), and load the
//             result into the editor as dirty. Bad JSON is rejected without touching
//             the current editor model.
//
// Two deliberate, documented choices vs the legacy:
//   1. Structure check: a parsed value that is not a plain object (a primitive,
//      array, or null) is rejected as invalid rather than silently producing a blank
//      monster (legacy was lenient here). This honours the brief's "validate
//      structure" without changing valid-file behaviour.
//   2. Feedback: success and failure are surfaced through the studio toast rather
//      than a blocking alert (matching how Stage 58 reports CRUD outcomes). Behaviour
//      is otherwise identical.
//
// The only DOM use is the standard browser download idiom (a transient anchor) and
// the hidden file input via a ref - the legacy mechanism, unavoidable for file IO,
// and never the application render tree. No backend, no localStorage. No optional
// chaining. No em dash.

import { useState, useRef } from 'react';
import {
  normalizeStudioMonster,
  nextMonsterId
} from '../../../monster/monsterStudioEngine';

// ---- Pure helpers ----------------------------------------------------------

// Build the export payload from a model (viStudioExport body). Pure: returns the
// filename and the pretty JSON string; performs no IO.
export function buildExport(model) {
  var m = model || {};
  var base = (m.customName || m.name || 'monster').replace(/[^a-z0-9]+/gi, '_');
  return { filename: base + '.json', json: JSON.stringify(m, null, 2) };
}

// Parse + validate + normalize an import payload (viStudioImportFile body, minus the
// IO). Pure: takes the raw text and a fallback id, returns { ok, model } or
// { ok:false, error }. The current editor model is never referenced or mutated here.
export function parseImport(text, fallbackId) {
  var obj;
  try {
    obj = JSON.parse(text);
  } catch (e) {
    return { ok: false, error: 'Invalid JSON file' };
  }
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
    return { ok: false, error: 'Invalid JSON file' };
  }
  var s = normalizeStudioMonster(obj, fallbackId);
  if (!obj.customMonsterId) {
    s.customMonsterId = fallbackId;
    s.id = fallbackId;
  }
  s.versions = Array.isArray(obj.versions) ? obj.versions : [];
  return { ok: true, model: s };
}

// ---- Hook ------------------------------------------------------------------

function useMonsterStudioImportExport(props) {
  const editor = props.editor;
  const library = props.library;

  const inputRef = useRef(null);
  const [message, setMessage] = useState(null);
  const messageIdRef = useRef(0);
  const [messageId, setMessageId] = useState(0);

  function toast(text) {
    messageIdRef.current = messageIdRef.current + 1;
    setMessage(text);
    setMessageId(messageIdRef.current);
  }

  // viStudioExport: serialise the live editor model and trigger a download.
  function exportJson() {
    if (typeof document === 'undefined' || typeof Blob === 'undefined' || typeof URL === 'undefined') { return false; }
    try {
      var data = buildExport(editor.model);
      var blob = new Blob([data.json], { type: 'application/json' });
      var url = URL.createObjectURL(blob);
      var a = document.createElement('a');
      a.href = url;
      a.download = data.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      if (URL.revokeObjectURL) { URL.revokeObjectURL(url); }
      return true;
    } catch (e) {
      toast('Export failed');
      return false;
    }
  }

  // viStudioImport: open the hidden file picker.
  function triggerImport() {
    var el = inputRef.current;
    if (el) { el.click(); }
  }

  // Read + apply a selected file (viStudioImportFile). On success the model loads
  // into the editor as dirty; on failure the editor is left untouched.
  function importFile(file) {
    if (!file) { return; }
    if (typeof FileReader === 'undefined') { return; }
    var reader = new FileReader();
    reader.onload = function() {
      var fallbackId = nextMonsterId(library.customMonsters, 0).id;
      var result = parseImport(String(reader.result), fallbackId);
      if (!result.ok) {
        toast(result.error);
        return;
      }
      editor.setModelDirty(result.model);
      toast('Imported (ID ' + result.model.customMonsterId + ')');
    };
    reader.readAsText(file);
  }

  // Hidden-input change handler: extract the file, reset the input so the same file
  // can be re-imported, then process.
  function onInputChange(e) {
    var input = e.target;
    var file = (input.files && input.files[0]) ? input.files[0] : null;
    input.value = '';
    importFile(file);
  }

  return {
    inputRef: inputRef,
    message: message,
    messageId: messageId,
    exportJson: exportJson,
    triggerImport: triggerImport,
    onInputChange: onInputChange,
    importFile: importFile
  };
}

export default useMonsterStudioImportExport;
